/**
 * Formato de fecha en shortcodes HTML: `[campo||df:DD/MM/YYYY]`.
 * También view.design.htmlDateFormats[ruta] como respaldo sin `df` en el shortcode.
 */

export const DEFAULT_HTML_SHORTCODE_DATE_FORMAT = "DD/MM/YYYY";

/** Meta de registro: siempre fecha en shortcodes HTML (aunque no esté en ModelDefinition). */
export const HTML_SHORTCODE_CREATED_AT_KEY = "createdAt";

/**
 * @param {string} fieldKey
 * @param {{ type?: string }|null|undefined} field
 * @param {string[]} [nestedPath]
 */
export function isHtmlShortcodeDateField(fieldKey, field, nestedPath = []) {
  if (nestedPath && nestedPath.length > 0) {
    return field?.type === "date";
  }
  return fieldKey === HTML_SHORTCODE_CREATED_AT_KEY || field?.type === "date";
}

export const HTML_SHORTCODE_DATE_FORMAT_PRESETS = [
  { value: "DD/MM/YYYY", label: "31/12/2026" },
  { value: "DD/MM/YYYY HH:mm", label: "31/12/2026 14:30" },
  { value: "MM/DD/YYYY", label: "12/31/2026" },
  { value: "YYYY-MM-DD", label: "2026-12-31" },
  { value: "D/M/YYYY", label: "31/12/2026 (sin ceros)" },
  { value: "DD-MM-YYYY", label: "31-12-2026" },
  { value: "MMMM YYYY", label: "diciembre 2026" },
  { value: "DD MMMM YYYY", label: "31 diciembre 2026" },
];

const MONTH_NAMES_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/**
 * @param {string} fullSpec - Interior del shortcode sin corchetes (puede incluir `||`).
 * @returns {string|null}
 */
export function parseHtmlShortcodeDateFormatFromExtras(fullSpec) {
  const parts = String(fullSpec || "").split("||");
  for (let i = 1; i < parts.length; i++) {
    const ch = parts[i].trim();
    if (!ch) continue;
    if (ch.toLowerCase().startsWith("df:")) {
      const fmt = ch.slice(3).trim();
      if (fmt) return fmt;
    }
  }
  return null;
}

/** Quita segmentos `||df:…` del inner del shortcode. */
export function shortcodeInnerWithoutDateFormatExtra(inner) {
  const parts = String(inner || "").split("||");
  const kept = [parts[0]];
  for (let i = 1; i < parts.length; i++) {
    const ch = parts[i].trim();
    if (ch && !ch.toLowerCase().startsWith("df:")) {
      kept.push(parts[i]);
    }
  }
  return kept.join("||").trim();
}

/**
 * @param {string} inner
 * @param {string|null|undefined} dateFormat
 * @returns {string}
 */
export function applyDateFormatExtraToShortcodeInner(inner, dateFormat) {
  const without = shortcodeInnerWithoutDateFormatExtra(inner);
  const fmt = String(dateFormat || "").trim();
  if (!fmt || fmt === DEFAULT_HTML_SHORTCODE_DATE_FORMAT) {
    return without;
  }
  return `${without}||df:${fmt}`;
}

/**
 * @param {object} opts
 * @param {string|null|undefined} opts.parsedDateFormat - Desde `||df:` en el shortcode.
 * @param {object|null|undefined} opts.viewDesign
 * @param {string} opts.basePath - Ruta `campo` o `grupo>fecha`.
 */
export function resolveHtmlShortcodeDateFormat({
  parsedDateFormat,
  viewDesign,
  basePath,
}) {
  if (parsedDateFormat) return parsedDateFormat;
  const fromDesign = viewDesign?.htmlDateFormats?.[basePath];
  if (fromDesign != null && String(fromDesign).trim() !== "") {
    return String(fromDesign).trim();
  }
  return DEFAULT_HTML_SHORTCODE_DATE_FORMAT;
}

/**
 * Extrae partes calendario/hora sin cambiar el día civil (ISO YYYY-MM-DD o UTC en Date).
 * @param {string|Date} value
 * @returns {{ y: number, m: number, d: number, h: number, min: number, s: number }|null}
 */
export function parseDatePartsForHtmlFormat(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  try {
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return null;
      if (s.includes("T")) {
        const [datePart, timePart] = s.split("T");
        if (datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          const [y, m, d] = datePart.split("-").map(Number);
          let h = 0;
          let min = 0;
          let sec = 0;
          if (timePart) {
            const tm = timePart.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
            if (tm) {
              h = Number(tm[1]);
              min = Number(tm[2]);
              sec = tm[3] != null ? Number(tm[3]) : 0;
            }
          }
          return { y, m, d, h, min, s: sec };
        }
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split("-").map(Number);
        return { y, m, d, h: 0, min: 0, s: 0 };
      }
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return null;
    return {
      y: date.getUTCFullYear(),
      m: date.getUTCMonth() + 1,
      d: date.getUTCDate(),
      h: date.getUTCHours(),
      min: date.getUTCMinutes(),
      s: date.getUTCSeconds(),
    };
  } catch (e) {
    return null;
  }
}

/**
 * @param {string|Date} value
 * @param {string} pattern
 * @param {string} [fallback]
 * @returns {string}
 */
export function formatDateWithHtmlPattern(value, pattern, fallback = "") {
  const parts = parseDatePartsForHtmlFormat(value);
  if (!parts) return fallback;
  const pat = String(pattern || DEFAULT_HTML_SHORTCODE_DATE_FORMAT).trim();
  if (!pat) return fallback;

  const { y, m, d, h, min, s } = parts;
  const monthName = MONTH_NAMES_ES[m - 1] || "";
  const monthNameCap =
    monthName.length > 0
      ? monthName.charAt(0).toUpperCase() + monthName.slice(1)
      : "";

  const tokens = {
    YYYY: String(y),
    YY: String(y).slice(-2),
    MMMM: monthNameCap,
    MMM: monthNameCap.slice(0, 3),
    MM: String(m).padStart(2, "0"),
    M: String(m),
    DD: String(d).padStart(2, "0"),
    D: String(d),
    HH: String(h).padStart(2, "0"),
    H: String(h),
    mm: String(min).padStart(2, "0"),
    m: String(min),
    ss: String(s).padStart(2, "0"),
  };

  const tokenOrder = [
    "YYYY",
    "YY",
    "MMMM",
    "MMM",
    "MM",
    "DD",
    "HH",
    "mm",
    "ss",
    "M",
    "D",
    "H",
    "m",
  ];
  let out = "";
  let i = 0;
  while (i < pat.length) {
    let matched = null;
    for (const tok of tokenOrder) {
      if (pat.startsWith(tok, i)) {
        matched = tok;
        break;
      }
    }
    if (matched) {
      out += tokens[matched];
      i += matched.length;
    } else {
      out += pat[i];
      i += 1;
    }
  }
  return out;
}
